import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Store, ArrowLeft, Package, Clock, CheckCircle, XCircle, Truck, ChevronDown, ChevronUp, RefreshCw, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { customerAuthAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

export default function CustomerOrders() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedOrder, setExpandedOrder] = useState(null);

  // Get store context from URL params or referrer
  const searchParams = new URLSearchParams(location.search);
  const storeSlug = searchParams.get('store') || localStorage.getItem('lastVisitedStore');

  useEffect(() => {
    if (!user || user.role !== 'customer') {
      const redirectUrl = storeSlug 
        ? `/customer/login?redirect=/customer/orders?store=${storeSlug}` 
        : '/customer/login?redirect=/customer/orders';
      navigate(redirectUrl);
      return;
    }
    fetchOrders();
  }, [user, navigate, page, statusFilter]);

  const fetchOrders = async () => {
    try {
      const response = await customerAuthAPI.getOrders({
        page,
        page_size: 10,
        status_filter: statusFilter !== 'all' ? statusFilter : undefined
      });
      setOrders(response.data.items);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'accepted':
        return <Badge className="bg-secondary text-secondary-foreground"><CheckCircle className="h-3 w-3 mr-1" />Accepted</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getDeliveryBadge = (status) => {
    const statusMap = {
      'pending': { label: 'Pending', className: 'bg-gray-100 text-gray-700' },
      'processing': { label: 'Processing', className: 'bg-blue-50 text-blue-700' },
      'dispatched': { label: 'Dispatched', className: 'bg-purple-50 text-purple-700' },
      're_dispatched': { label: 'Re-dispatched', className: 'bg-purple-50 text-purple-700' },
      'delivered': { label: 'Delivered', className: 'bg-green-50 text-green-700' },
	  'cancelled': { label: 'Cancelled', className: 'bg-red-50 text-red-700' },
	  'returned':  { label: 'Return Received', className: 'bg-amber-50 text-amber-700' },
	  'refunding': { label: 'Refund Initiated', className: 'bg-orange-50 text-orange-700' }
    };
    const config = statusMap[status] || statusMap['pending'];
    return <Badge variant="outline" className={config.className}><Truck className="h-3 w-3 mr-1" />{config.label}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user || user.role !== 'customer') {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to={storeSlug ? `/shop/${storeSlug}` : '/'}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              <span className="hidden sm:inline">{storeSlug ? 'Back to Store' : 'Back to Home'}</span>
            </Link>
            <div className="flex items-center gap-2">
              <Store className="h-6 w-6 text-primary" />
              <span className="font-heading font-bold">My Orders</span>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={fetchOrders}>
            <RefreshCw className="h-5 w-5" />
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Orders</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-6 bg-muted rounded w-1/4 mb-4" />
                  <div className="h-4 bg-muted rounded w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : orders.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Package className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="font-heading font-semibold mb-2">No orders yet</h3>
              <p className="text-muted-foreground text-sm mb-4">Start shopping to see your orders here</p>
              <Link to={storeSlug ? `/shop/${storeSlug}` : '/'}>
                <Button>{storeSlug ? 'Continue Shopping' : 'Browse Shops'}</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <Collapsible 
                key={order.id}
                open={expandedOrder === order.id}
                onOpenChange={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
              >
                <Card className={`border-border/50 ${order.status === 'accepted' ? 'border-l-4 border-l-secondary' : order.status === 'rejected' ? 'border-l-4 border-l-destructive' : ''}`}>
                  <CollapsibleTrigger className="w-full text-left">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-mono text-sm text-muted-foreground">#{order.id.substring(0, 8)}</span>
                            {getStatusBadge(order.status)}
                            {order.status === 'accepted' && getDeliveryBadge(order.delivery_status)}
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{order.seller_name}</p>
                            {order.shop_slug && (
                              <Link 
                                to={`/shop/${order.shop_slug}`} 
                                onClick={(e) => e.stopPropagation()}
                                className="text-primary hover:underline text-xs flex items-center gap-1"
                              >
                                <ExternalLink className="h-3 w-3" />
                                Visit Store
                              </Link>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{formatDate(order.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-heading font-bold text-lg">₹{order.total_amount}</p>
                            <p className="text-xs text-muted-foreground uppercase">{order.payment_method}</p>
                          </div>
                          {expandedOrder === order.id ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>

                      {order.status === 'rejected' && order.rejection_reason && (
                        <div className="mt-3 p-3 bg-destructive/10 rounded-lg">
                          <p className="text-sm text-destructive">
                            <strong>Rejection Reason:</strong> {order.rejection_reason}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="px-4 pb-4 border-t border-border pt-4">
                      {/* Order Items */}
                      <div className="mb-4">
                        <h4 className="font-medium mb-2">Items</h4>
                        <div className="space-y-2">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span>{item.product_name} × {item.quantity}</span>
                              <span>₹{item.subtotal}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Delivery Address */}
                      <div className="mb-4">
                        <h4 className="font-medium mb-2">Delivery Address</h4>
                        <p className="text-sm text-muted-foreground">{order.customer_address}</p>
                      </div>

                      {/* Delivery Status History */}
                      {order.delivery_history && order.delivery_history.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-2">Delivery Updates</h4>
                          <div className="space-y-2">
                            {order.delivery_history.map((entry, idx) => (
                              <div key={idx} className="flex items-start gap-3 text-sm">
                                <div className="h-2 w-2 rounded-full bg-primary mt-1.5" />
                                <div>
                                  <p className="font-medium">
									  {(() => {
										const key = (entry.status || '').trim().toLowerCase();
										const labels = {
										  pending: 'Pending',
										  processing: 'Processing',
										  dispatched: 'Dispatched',
										  re_dispatched: 'Re-dispatched',
										  delivered: 'Delivered',
										  cancelled: 'Cancelled',
										  returned: 'Return Received',
										  refunding: 'Refund Initiated',
										};
										return labels[key] || (entry.status || '').replaceAll('_', ' ');
									  })()}
									</p>

                                  {entry.description && <p className="text-muted-foreground">{entry.description}</p>}
                                  <p className="text-xs text-muted-foreground">{formatDate(entry.timestamp)}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                <Button 
                  variant="outline" 
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="flex items-center px-4 text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button 
                  variant="outline"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
